import { subDays, toDate } from "date-fns";

import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { QUESTION_STATUSES_DEFAULT_STATUSES, setLeadsSnoozedFilters, useLeadsSnoozedFilters } from "modules/LeadsDashboard/Leads/SnoozedLeads/hooks";
import { LEADS_SNOOZED_STORAGE_KEYS } from "modules/LeadsDashboard/storageKeys";
import { PAGE_SIZE, setPreference } from "utils/common";

import type { DealersLocationsDropdownValue } from "components";

export const useSnoozedFilterHandlers = () => {
  const { selectedLocation } = useSelectedLocation();
  const filters = useLeadsSnoozedFilters();

  const handleTagsChange = (value: number[]) => {
    setLeadsSnoozedFilters({ ...filters, tags: value, page: 1 });
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.TagsPreference, value);
  };

  const handleSearchChange = (value: string) => setLeadsSnoozedFilters({ ...filters, search_term: value, page: 1 });

  const handleDepartmentChange = (value: number) => {
    setLeadsSnoozedFilters({ ...filters, snooze_department_id: value ? value : null, page: 1 });
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.DepartmentPreference, value ? value : null);
  };

  const handleResetFilters = () => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.DealerPreference, []);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.LocationPreference, selectedLocation?.id ? [selectedLocation.id] : []);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.CustomerApprovedPreference, false);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.InterventionsPreference, false);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.QuestionStatusPreference, QUESTION_STATUSES_DEFAULT_STATUSES);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.InterventionQuestionResultPreference, null);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.TagsPreference, null);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.DepartmentPreference, null);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.SnoozeStatusPreference, null);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.TaskPreference, null);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.CustomerOkUserPreference, false);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.RegVinPreference, null);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.PageSizePreference, PAGE_SIZE.PageSize25);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.AppointmentDatePreference, { start: subDays(new Date(), 30), end: new Date() });
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.SnoozeDatePreference, { start: null, end: null });
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.UpdatedOnDatePreference, { start: null, end: null });

    setLeadsSnoozedFilters({
      ...filters,
      page: 1,
      dealer_ids: [],
      appointment_date_from: subDays(new Date(), 30),
      appointment_date_to: toDate(new Date()),
      search_term: "",
      is_lease_company: null,
      location_ids: selectedLocation?.id ? [selectedLocation.id] : [],
      customer_approved: false,
      interventions: false,
      statuses: QUESTION_STATUSES_DEFAULT_STATUSES,
      questions: null,
      tags: null,
      snooze_date_from: null,
      snooze_date_to: null,
      snooze_department_id: null,
      snooze_status_id: null,
      snooze_type_id: null,
      last_customer_ok_is_me: false,
      updated_on_from: null,
      updated_on_to: null,
      page_size: PAGE_SIZE.PageSize25
    });
  };
  const handleInterventionsChange = () => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.InterventionsPreference, !filters.interventions);
    setLeadsSnoozedFilters({ ...filters, interventions: !filters.interventions, page: 1 });
  };

  const handleCustomerApprovedChange = () => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.CustomerApprovedPreference, !filters.customer_approved);
    setLeadsSnoozedFilters({ ...filters, customer_approved: !filters.customer_approved, page: 1 });
  };

  const handleDealerLocationChange = ({ dealerIds, locationIds }: DealersLocationsDropdownValue) => {
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.DealerPreference, dealerIds);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.LocationPreference, locationIds);
    setPreference(LEADS_SNOOZED_STORAGE_KEYS.InterventionQuestionResultPreference, null);
    setLeadsSnoozedFilters({ ...filters, dealer_ids: dealerIds, location_ids: locationIds, questions: null, page: 1 });
  };

  return {
    handleTagsChange,
    handleResetFilters,
    handleSearchChange,
    handleDepartmentChange,
    handleInterventionsChange,
    handleDealerLocationChange,
    handleCustomerApprovedChange
  };
};
